import React, { useEffect, useRef, useState } from "react";
import axiosInstance from "../api/axiosInstance";

const toTitleCase = (text) => {
  if (!text) return "";
  return text
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .replace(/\s+/g, " ")
    .trim();
};

const AddEditEmployeeOptionalFields = ({ employeeData, onClose, onSuccess }) => {
  const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
  const groupId = storedUser?.groupId || 1;
  const userId = storedUser?.userId || 0;

  /* ================= STATE ================= */
  const [birthDate, setBirthDate] = useState("");
  const [anniversaryDate, setAnniversaryDate] = useState("");
  const [aadharCardNo, setAadharCardNo] = useState("");
  const [panNo, setPanNo] = useState("");
  const [policeVerificationDate, setPoliceVerificationDate] = useState("");
  const [policeVerificationNo, setPoliceVerificationNo] = useState("");
  const [referenceBy, setReferenceBy] = useState("");
  const [referenceMobileNo, setReferenceMobileNo] = useState("");
  const [relievingDate, setRelievingDate] = useState("");
  const [loading, setLoading] = useState(false);

  /* ================= REFS ================= */
  const birthRef = useRef(null);
  const annivRef = useRef(null);
  const aadharRef = useRef(null);
  const panRef = useRef(null);
  const policeDateRef = useRef(null);
  const policeNoRef = useRef(null);
  const refByRef = useRef(null);
  const refMobileRef = useRef(null);
  const relievingRef = useRef(null);
  const saveRef = useRef(null);

  const formatPoliceNo = (value) => {
  return value
    .toUpperCase()
    .replace(/[^A-Z0-9/-]/g, "")
    .slice(0, 20);
};

  /* ================= LOAD DATA ================= */
  useEffect(() => {
    if (!employeeData) return;

    setBirthDate(employeeData.birthDate?.substring(0, 10) || "");
    setAnniversaryDate(employeeData.anniversaryDate?.substring(0, 10) || "");
    setAadharCardNo(employeeData.aadharCardNo || "");
    setPanNo(employeeData.panNo || "");
    setPoliceVerificationDate(employeeData.policeVerificationDate?.substring(0, 10) || "");
    setPoliceVerificationNo(employeeData.policeVerificationNo || "");
    setReferenceBy(employeeData.referenceBy || "");
    setReferenceMobileNo(employeeData.referenceMobileNo || "");
    setRelievingDate(employeeData.relievingDate?.substring(0, 10) || "");
  }, [employeeData]);

  /* ================= AUTO FOCUS + SELECT ================= */
  useEffect(() => {
    setTimeout(() => {
      birthRef.current?.focus();
      birthRef.current?.select();
    }, 100);
  }, []);

  const handleFocusSelect = (ref) => {
    setTimeout(() => {
      ref?.current?.select();
    }, 0);
  };

  /* ================= KEYBOARD NAV ================= */
  const handleKeyDown = (e, nextRef, prevRef, options = {}) => {
    const { isLast = false, mobileCheck = false } = options;

    // Mobile field: only allow Enter if 10 digits
    if (mobileCheck && e.key === "Enter" && referenceMobileNo.length !== 10) {
      e.preventDefault();
      return;
    }

    if (e.key === "Enter") {
      e.preventDefault();
      if (nextRef?.current) nextRef.current.focus();
      else if (isLast) handleSubmit();
    }

    if (e.key === "Escape") {
      e.preventDefault();
      if (prevRef?.current) prevRef.current.focus();
      else onClose?.();
    }
  };

  /* ================= FORMAT TEXT ================= */
  const handleBlurFormat = (setter) => (e) => {
    setter(toTitleCase(e.target.value));
  };

  /* ================= SUBMIT ================= */
  const handleSubmit = async () => {
    setLoading(true);

    const payload = {
      empId: employeeData?.empId || 0,
      groupId,
      userId,
      empImageFileName: employeeData?.empImageFileName || "",
      birthDate: birthDate ? new Date(birthDate).toISOString() : null,
      anniversaryDate: anniversaryDate ? new Date(anniversaryDate).toISOString() : null,
      aadharCardNo,
      panNo, // keep original
      policeVerificationDate: policeVerificationDate ? new Date(policeVerificationDate).toISOString() : null,
      policeVerificationNo,
      referenceBy,
      referenceMobileNo,
      relievingDate: relievingDate ? new Date(relievingDate).toISOString() : null,
    };

    try {
      await axiosInstance.post("/EmployeeMaster/UpdateEmployeeOptionalFields", payload);
      onSuccess?.();
      onClose?.();
    } catch (err) {
      console.error("Optional save error:", err);
      alert("Save failed");
    } finally {
      setLoading(false);
    }
  };

  /* ================= UI ================= */
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[9999] p-3" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl rounded-xl shadow-xl p-6" onClick={(e) => e.stopPropagation()}>
        <h2 className="text-lg font-bold mb-4">Employee Optional Fields</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <InputDate label="Birth Date" value={birthDate} setValue={setBirthDate} refProp={birthRef} nextRef={annivRef} prevRef={null} handleKeyDown={handleKeyDown} handleFocusSelect={handleFocusSelect} />
          <InputDate label="Anniversary Date" value={anniversaryDate} setValue={setAnniversaryDate} refProp={annivRef} nextRef={aadharRef} prevRef={birthRef} handleKeyDown={handleKeyDown} handleFocusSelect={handleFocusSelect} />
          <InputText label="Aadhar No" value={aadharCardNo} setValue={setAadharCardNo} refProp={aadharRef} nextRef={panRef} prevRef={annivRef} handleKeyDown={handleKeyDown} handleBlurFormat={handleBlurFormat(setAadharCardNo)} handleFocusSelect={handleFocusSelect} />
          <InputText label="PAN No" value={panNo} setValue={setPanNo} refProp={panRef} nextRef={policeDateRef} prevRef={aadharRef} handleKeyDown={handleKeyDown} handleFocusSelect={handleFocusSelect} /> {/* PAN no format */}
          <InputDate label="Police Verification Date" value={policeVerificationDate} setValue={setPoliceVerificationDate} refProp={policeDateRef} nextRef={policeNoRef} prevRef={panRef} handleKeyDown={handleKeyDown} handleFocusSelect={handleFocusSelect} />
         <InputText
  label="Police Verification No"
  value={policeVerificationNo}
  setValue={(val) => setPoliceVerificationNo(formatPoliceNo(val))}
  refProp={policeNoRef}
  nextRef={refByRef}
  prevRef={policeDateRef}
  handleKeyDown={handleKeyDown}
  handleFocusSelect={handleFocusSelect}
/>
          <InputText label="Reference By" value={referenceBy} setValue={setReferenceBy} refProp={refByRef} nextRef={refMobileRef} prevRef={policeNoRef} handleKeyDown={handleKeyDown} handleBlurFormat={handleBlurFormat(setReferenceBy)} handleFocusSelect={handleFocusSelect} />
          <InputText
            label="Reference Mobile"
            value={referenceMobileNo}
            setValue={(val) => setReferenceMobileNo(val.replace(/\D/g, "").slice(0, 10))}
            refProp={refMobileRef}
            nextRef={relievingRef}
            prevRef={refByRef}
            handleKeyDown={(e) => handleKeyDown(e, relievingRef, refByRef, { mobileCheck: true })}
            handleFocusSelect={handleFocusSelect}
          />
          <InputDate label="Relieving Date" value={relievingDate} setValue={setRelievingDate} refProp={relievingRef} nextRef={saveRef} prevRef={refMobileRef} handleKeyDown={handleKeyDown} handleFocusSelect={handleFocusSelect} />
        </div>

        <div className="flex justify-end gap-3 mt-5">
          <button onClick={onClose} className="px-4 py-2 rounded-lg bg-gray-500 text-white hover:bg-gray-600">Cancel</button>
          <button ref={saveRef} onClick={handleSubmit} onKeyDown={(e) => handleKeyDown(e, null, relievingRef, { isLast: true })} className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700">
            {loading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ================= SMALL INPUTS ================= */
const InputText = ({ label, value, setValue, refProp, nextRef, prevRef, handleKeyDown, handleBlurFormat, handleFocusSelect }) => (
  <div>
    <label className="block text-sm font-medium mb-1">{label}</label>
    <input
      ref={refProp}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={handleBlurFormat}
      onFocus={() => handleFocusSelect(refProp)}
      onKeyDown={(e) => handleKeyDown(e, nextRef, prevRef)}
      className="w-full border rounded-lg px-3 py-2"
    />
  </div>
);

const InputDate = ({ label, value, setValue, refProp, nextRef, prevRef, handleKeyDown, handleFocusSelect }) => (
  <div>
    <label className="block text-sm font-medium mb-1">{label}</label>
    <input
      ref={refProp}
      type="date"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onFocus={() => handleFocusSelect(refProp)}
      onKeyDown={(e) => handleKeyDown(e, nextRef, prevRef)}
      className="w-full border rounded-lg px-3 py-2"
    />
  </div>
);

export default AddEditEmployeeOptionalFields;